/**
 * The sections of System settings — the one source of their menu, overview, search, redirects,
 * AI context and old tab ids.
 */
import type { IconName } from '@insio/ui-toolkit';
import { Permission } from '@insio/shared-types';
import { FeatureFlagKey } from '@/features/feature-flags/feature-flag-keys';
import { navigation } from '@/navigation';
import {
  accountActions,
  hasAnyAccountAction,
  type AccountActionsSubject
} from '@/features/admin/components/system-settings/account/account-actions';
import { canOpenAutomationSettings } from '@/features/admin/components/system-settings/automation/can-open-automation-settings';

/** What a section's visibility is decided from. */
export interface SettingsVisibilityContext {
  readonly hasPermission: (permission: Permission) => boolean;
  readonly isFlagEnabled: (flag: FeatureFlagKey) => boolean;
  /** The account is a client of a master account, which is billed instead. */
  readonly isManagedClient: boolean;
  /** The signed-in account from the profile; undefined or null while it is unknown. */
  readonly account: AccountActionsSubject | null | undefined;
}

/** Menu groups, in the order the menu and the overview show them. */
export const SETTINGS_GROUP_IDS = [
  'company',
  'people',
  'documents',
  'codeLists',
  'connections',
  'subscription'
] as const;

export type SettingsGroupId = (typeof SETTINGS_GROUP_IDS)[number];

/** In place of a group: under the groups, set apart, for rare or destructive settings. */
export const SETTINGS_FOOTER = 'footer';

/** The tabs over System settings, in order. */
export const SETTINGS_TAB_IDS = ['settings', 'codeLists', 'automation'] as const;

export type SettingsTabId = (typeof SETTINGS_TAB_IDS)[number];

/** The side menu of a tab: its sections, and the screen where it stands alone on a phone. */
export interface SettingsTabMenu {
  /** The menu on its own: the list screen on a phone. */
  readonly path: () => string;
  /** True where a desktop shows the overview beside it; otherwise the path opens the first section. */
  readonly overview: boolean;
  /** A menu of one group, titled by it and without a heading of its own. */
  readonly group?: SettingsGroupId;
}

export interface SettingsTabDefinition {
  /** None for a tab of one page across the full width. */
  readonly menu?: SettingsTabMenu;
}

export const SETTINGS_TABS: Readonly<Record<SettingsTabId, SettingsTabDefinition>> = {
  settings: { menu: { path: () => navigation.settings(), overview: true } },
  codeLists: {
    menu: { path: () => navigation.codeListsMenu(), overview: false, group: 'codeLists' }
  },
  automation: {}
};

/** A sub-item of a section on its own route, shown under it in the menu (Integrace: Instalace). */
export interface SettingsSubsectionDefinition {
  /** Key of its translations, under its section's `children`. */
  readonly id: string;
  /** Its own route, below its section's. */
  readonly path: () => string;
}

export interface SettingsSectionDefinition {
  /** Key of its translations and of the AI page context. */
  readonly id: string;
  /** Its URL segment: `/settings/<slug>`. */
  readonly slug: string;
  /** Its own routes at `/settings/<slug>`, with sub-pages, mounted in the shell by a layout route. */
  readonly path?: () => string;
  readonly group: SettingsGroupId | typeof SETTINGS_FOOTER;
  /** The tab it opens under. */
  readonly tab: SettingsTabId;
  readonly icon: IconName;
  /** The `?tab=` ids of the page it replaced (System settings or Code lists), redirected here. */
  readonly legacyTabs: readonly string[];
  /** Sub-items of a section with a `path`; the menu shows them while it or one of them is open. */
  readonly children?: readonly SettingsSubsectionDefinition[];
  readonly isVisible: (context: SettingsVisibilityContext) => boolean;
}

/** Visible to whoever holds the permission. */
const holds =
  (permission: Permission) =>
  ({ hasPermission }: SettingsVisibilityContext) =>
    hasPermission(permission);

// Number series and document output ship with P2P: the purchase order is their only document.
const managesProcurementSettings = ({ hasPermission, isFlagEnabled }: SettingsVisibilityContext) =>
  hasPermission(Permission.ACCOUNT_SETTINGS) && isFlagEnabled(FeatureFlagKey.PROCUREMENT);

/** Every section, in menu order; a new section is one more entry here. */
export const SETTINGS_SECTIONS = [
  {
    id: 'organization',
    slug: 'organization',
    group: 'company',
    tab: 'settings',
    icon: 'building',
    legacyTabs: ['organization'],
    isVisible: holds(Permission.ACCOUNT_SETTINGS)
  },
  {
    id: 'bankAccounts',
    slug: 'bank-accounts',
    group: 'company',
    tab: 'settings',
    icon: 'wallet',
    legacyTabs: ['bankAccounts'],
    // Anyone reads it; the switch and the accounts' actions check their own permissions.
    isVisible: () => true
  },
  {
    id: 'users',
    slug: 'users',
    group: 'people',
    tab: 'settings',
    icon: 'user',
    legacyTabs: ['users'],
    isVisible: holds(Permission.USERS_MANAGE)
  },
  {
    id: 'mailbox',
    slug: 'mailbox',
    group: 'documents',
    tab: 'settings',
    icon: 'envelope',
    legacyTabs: ['mailbox'],
    // Same rule as the backend gate on the mailbox lifecycle.
    isVisible: holds(Permission.ACCOUNT_SETTINGS)
  },
  {
    id: 'automation',
    slug: 'automation',
    group: 'documents',
    tab: 'automation',
    icon: 'bolt',
    legacyTabs: ['automation'],
    isVisible: ({ hasPermission }) => canOpenAutomationSettings(hasPermission)
  },
  {
    id: 'numberSeries',
    slug: 'number-series',
    group: 'documents',
    tab: 'settings',
    icon: 'list-ul',
    legacyTabs: ['numberSeries'],
    isVisible: managesProcurementSettings
  },
  {
    id: 'documentOutput',
    slug: 'document-output',
    group: 'documents',
    tab: 'settings',
    icon: 'file-lines',
    legacyTabs: ['documents'],
    isVisible: managesProcurementSettings
  },
  // Anyone reads the code lists; their actions check their own permissions.
  {
    id: 'partners',
    slug: 'partners',
    // Annotated: navigation.ts reads its types from this registry.
    path: (): string => navigation.partners(),
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'building',
    legacyTabs: [],
    isVisible: () => true
  },
  {
    id: 'orgStructure',
    slug: 'code-lists/org-structure',
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'diagram-project',
    legacyTabs: ['orgStructure'],
    isVisible: () => true
  },
  {
    id: 'costCenters',
    slug: 'code-lists/cost-centers',
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'coins',
    legacyTabs: ['costCenters'],
    isVisible: () => true
  },
  {
    id: 'jobs',
    slug: 'code-lists/jobs',
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'briefcase',
    legacyTabs: ['jobs'],
    isVisible: () => true
  },
  {
    id: 'glPostings',
    slug: 'code-lists/gl-postings',
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'table',
    legacyTabs: ['glPostings'],
    isVisible: () => true
  },
  {
    id: 'vatCodes',
    slug: 'code-lists/vat-codes',
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'percent',
    legacyTabs: ['vatCodes'],
    isVisible: () => true
  },
  {
    id: 'vatPostings',
    slug: 'code-lists/vat-postings',
    group: 'codeLists',
    tab: 'codeLists',
    icon: 'sliders',
    legacyTabs: ['vatPostings'],
    isVisible: () => true
  },
  // Decided before the menu loads, as its rule needs a permission only; each page keeps its check.
  {
    id: 'integrations',
    slug: 'integrations',
    path: (): string => navigation.integrations(),
    group: 'connections',
    tab: 'settings',
    icon: 'plug',
    legacyTabs: [],
    children: [
      { id: 'connections', path: (): string => navigation.integrationConnections() },
      { id: 'bridges', path: (): string => navigation.integrationBridges() },
      { id: 'monitor', path: (): string => navigation.integrationMonitor() }
    ],
    isVisible: holds(Permission.INTEGRATIONS_MANAGE)
  },
  {
    id: 'webhooks',
    slug: 'webhooks',
    group: 'connections',
    tab: 'settings',
    icon: 'link',
    legacyTabs: ['webhooks'],
    isVisible: holds(Permission.WEBHOOKS_MANAGE)
  },
  {
    id: 'apiKeys',
    slug: 'api-keys',
    group: 'connections',
    tab: 'settings',
    icon: 'key',
    legacyTabs: ['apiKeys'],
    isVisible: holds(Permission.API_KEYS_MANAGE)
  },
  {
    id: 'subscription',
    slug: 'subscription',
    group: 'subscription',
    tab: 'settings',
    icon: 'credit-card',
    legacyTabs: ['subscription', 'payments'],
    isVisible: holds(Permission.BILLING_MANAGE)
  },
  {
    id: 'billingInvoices',
    slug: 'billing-invoices',
    group: 'subscription',
    tab: 'settings',
    icon: 'file-invoice-dollar',
    legacyTabs: ['invoices'],
    isVisible: ({ hasPermission, isManagedClient }) =>
      hasPermission(Permission.BILLING_MANAGE) && !isManagedClient
  },
  {
    id: 'accountManagement',
    slug: 'account',
    group: SETTINGS_FOOTER,
    tab: 'settings',
    icon: 'gear',
    legacyTabs: [],
    // Same permission as the company profile, and only while one of its actions applies.
    isVisible: ({ hasPermission, account }) =>
      hasPermission(Permission.ACCOUNT_SETTINGS) &&
      !!account &&
      hasAnyAccountAction(accountActions(account, hasPermission(Permission.ACCOUNT_SETTINGS)))
  }
] as const satisfies readonly SettingsSectionDefinition[];

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];
export type SettingsSectionId = SettingsSection['id'];

/** A section on its own routes, with sub-pages (Firmy, Integrace). */
export type SettingsPathSection = Extract<SettingsSection, { readonly path: () => string }>;

/** A section with sub-items in the menu (Integrace). */
export type SettingsParentSection = Extract<
  SettingsSection,
  { readonly children: readonly SettingsSubsectionDefinition[] }
>;

/** Id of a sub-item, unique within its section. */
export type SettingsSubsectionId = SettingsParentSection['children'][number]['id'];

/** A section opened at `/settings/<slug>`. */
export type SettingsSlugSection = Exclude<SettingsSection, SettingsPathSection>;

/** Slug of a section opened at `/settings/<slug>`. */
export type SettingsSectionSlug = SettingsSlugSection['slug'];

type CodeListSlugOf<Slug> = Slug extends `code-lists/${infer List}` ? List : never;

/** Slug of one code list, below `/settings/code-lists/`. */
export type CodeListSlug = CodeListSlugOf<SettingsSectionSlug>;

/** The overview at `/settings`: in the menu without a group, visible to everyone. */
export const SETTINGS_OVERVIEW = { id: 'overview', icon: 'house' } as const;

/** Id of whatever `/settings…` shows: the overview or a section. */
export type SettingsPageId = SettingsSectionId | typeof SETTINGS_OVERVIEW.id;

/** The tab a page opens under; the overview is the settings tab's. */
export function settingsTabOf(pageId: SettingsPageId): SettingsTabId {
  return SETTINGS_SECTIONS.find(section => section.id === pageId)?.tab ?? 'settings';
}

/** The sections of one tab, in menu order. */
export function settingsTabSections(
  sections: readonly SettingsSection[],
  tab: SettingsTabId
): readonly SettingsSection[] {
  return sections.filter(section => section.tab === tab);
}

/** The tabs to show: one with an overview always, any other while one of its sections is. */
export function visibleSettingsTabs(sections: readonly SettingsSection[]): SettingsTabId[] {
  return SETTINGS_TAB_IDS.filter(
    tab => SETTINGS_TABS[tab].menu?.overview || sections.some(section => section.tab === tab)
  );
}

/**
 * Where a tab opens: its menu where that is a screen of its own (the overview, a phone's list),
 * else its first section; none while it has no section to open.
 */
export function settingsTabHref(
  tab: SettingsTabId,
  sections: readonly SettingsSection[],
  isPhone: boolean
): string | undefined {
  const { menu } = SETTINGS_TABS[tab];
  if (menu && (isPhone || menu.overview)) return menu.path();
  const first = sections.find(section => section.tab === tab);
  return first ? settingsSectionHref(first) : undefined;
}

function isSlugSection(section: SettingsSection): section is SettingsSlugSection {
  return !('path' in section);
}

/** The section `/settings/<slug>` looks up; one on its own routes is matched by them instead. */
export function findSettingsSectionBySlug(
  slug: string | undefined
): SettingsSlugSection | undefined {
  return SETTINGS_SECTIONS.filter(isSlugSection).find(section => section.slug === slug);
}

/** Where a section opens: its own route, or `/settings/<slug>`. */
export function settingsSectionHref(section: SettingsSection): string {
  return isSlugSection(section) ? navigation.settings(section.slug) : section.path();
}

/** Sub-items of a section, in menu order; none for a section without any. */
export function settingsSubsections(
  section: SettingsSection
): readonly SettingsSubsectionDefinition[] {
  return 'children' in section ? section.children : [];
}

/** Id of a sub-item in the menu: unique across the whole menu, sections included. */
export function settingsSubsectionNavId(
  sectionId: SettingsSectionId,
  subsectionId: string
): string {
  return `${sectionId}.${subsectionId}`;
}

export function findSettingsSectionByLegacyTab(
  tab: string | null | undefined
): SettingsSection | undefined {
  if (!tab) return undefined;
  return SETTINGS_SECTIONS.find(section => section.legacyTabs.some(legacy => legacy === tab));
}

export function visibleSettingsSections(
  context: SettingsVisibilityContext
): readonly SettingsSection[] {
  return SETTINGS_SECTIONS.filter(section => section.isVisible(context));
}

/** What the visibility context still lacks; permissions are known at once. */
export interface SettingsPendingInputs {
  readonly flags: boolean;
  readonly profile: boolean;
}

/**
 * True when the section's visibility is final: nothing is pending, or its rule read none of what is
 * (a destructured input counts as read, so such a rule waits).
 */
export function isSettingsSectionDecided(
  section: SettingsSection,
  context: SettingsVisibilityContext,
  pending: SettingsPendingInputs
): boolean {
  let readPending = false;
  const tracked: SettingsVisibilityContext = {
    hasPermission: context.hasPermission,
    isFlagEnabled: flag => {
      readPending ||= pending.flags;
      return context.isFlagEnabled(flag);
    },
    get isManagedClient() {
      readPending ||= pending.profile;
      return context.isManagedClient;
    },
    get account() {
      readPending ||= pending.profile;
      return context.account;
    }
  };
  section.isVisible(tracked);
  return !readPending;
}
